import {
  Autocomplete,
  AutocompleteProps,
  TextField,
  TextFieldProps,
} from '@mui/material';
import { Control, Controller, FieldPath, FieldValues } from 'react-hook-form';

export interface MultiAutocompleteOption {
  id: string | number;
  name: string;
  disabled?: boolean;
}

export interface MultiAutocompleteSelectProps<TFieldValues extends FieldValues = FieldValues>
  extends Omit<
    AutocompleteProps<MultiAutocompleteOption, true, false, false>,
    'renderInput' | 'options' | 'value' | 'onChange' | 'multiple'
  > {
  name: FieldPath<TFieldValues>;
  control: Control<TFieldValues>;
  label?: string;
  disabled?: boolean;
  required?: boolean;
  helperText?: string;
  placeholder?: string;
  options: MultiAutocompleteOption[];
  textFieldProps?: Omit<TextFieldProps, 'error' | 'helperText'>;
}

export function MultiAutocompleteSelect<TFieldValues extends FieldValues = FieldValues>({
  name,
  control,
  label,
  disabled,
  required,
  helperText,
  placeholder,
  options,
  textFieldProps,
  ...autocompleteProps
}: MultiAutocompleteSelectProps<TFieldValues>) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const selectedOptions = options.filter(option => field.value?.includes(option.id)) || [];

        return (
          <Autocomplete
            {...autocompleteProps}
            multiple
            options={options}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={selectedOptions}
            onChange={(_event, newValues) => field.onChange(newValues.map(value => value.id))}
            disabled={disabled}
            renderInput={(params) => (
              <TextField
                {...params}
                {...textFieldProps}
                label={label}
                placeholder={placeholder}
                required={required}
                error={!!fieldState.error}
                helperText={fieldState.error?.message || helperText}
              />
            )}
          />
        );
      }}
    />
  );
}